<template>
  <div>
    <section-page
      title="Note Snippets"
      description="Create snippets to easily reuse blocks of text for your notes."
    >
      <template v-slot:right>
        <filter-link link-name="store" :link-params="{tab: 'snippets'}">Get snippets from Store</filter-link>
        <button-outlined icon-left="plus" @click="$refs.snippetNoteModal.open()">
          New Note Snippet
        </button-outlined>
      </template>

      <app-empty-message
        :count="noteSnippets.length"
        heading="You have no snippets yet"
      >
        <table-default
          :table-data="noteSnippets"
          default-sort="label"
        >
          <b-table-column label="Name" field="label" sortable v-slot="props">
            {{ props.row.label }}
          </b-table-column>

          <b-table-column label="Description" field="description" v-slot="props">
            {{ props.row.description }}
          </b-table-column>

          <b-table-column label="Body" field="body" sortable v-slot="props">
            {{ $utils.truncateString(props.row.body.replace(/(<([^>]+)>)/gi, ""), 70) }}
          </b-table-column>

          <b-table-column cell-class="action_cell" width="200" v-slot="props">
            <button-link @click="$refs.snippetNoteModal.open(props.row)">Manage</button-link>
            <button-link :loading="isDuplicatingNote" @click="duplicateNote(props.row.id)">Duplicate</button-link>
          </b-table-column>
        </table-default>
      </app-empty-message>
    </section-page>

    <section-page
      title="Email Snippets"
      description="Create snippets to easily reuse blocks of text for your emails."
    >
      <template v-slot:right>
        <filter-link link-name="store" :link-params="{tab: 'snippets'}">Get snippets from Store</filter-link>
        <button-outlined icon-left="plus" @click="$refs.snippetEmailModal.open()">
          New Email Snippet
        </button-outlined>
      </template>

      <app-empty-message
        :count="emailSnippets.length"
        heading="You have no snippets yet"
      >
        <table-default
          :table-data="emailSnippets"
          default-sort="label"
        >
          <b-table-column label="Name" field="label" sortable v-slot="props">
            {{ props.row.label }}
          </b-table-column>

          <b-table-column label="Description" field="description" v-slot="props">
            {{ props.row.description }}
          </b-table-column>

          <b-table-column label="Subject" field="subject" sortable v-slot="props">
            {{ $utils.truncateString(props.row.subject.replace(/(<([^>]+)>)/gi, ""), 70) }}
          </b-table-column>

          <b-table-column label="Body" field="body" sortable v-slot="props">
            {{ $utils.truncateString(props.row.body.replace(/(<([^>]+)>)/gi, ""), 70) }}
          </b-table-column>

          <b-table-column cell-class="action_cell" width="200" v-slot="props">
            <button-link @click="$refs.snippetEmailModal.open(props.row)">Manage</button-link>
            <button-link :loading="isDuplicatingEmail" @click="duplicateEmail(props.row.id)">Duplicate</button-link>
          </b-table-column>
        </table-default>
      </app-empty-message>
    </section-page>

    <section-page
      title="Text Snippets"
      description="Create snippets to easily reuse blocks of text for your texts."
    >
      <template v-slot:right>
        <filter-link link-name="store" :link-params="{tab: 'snippets'}">Get snippets from Store</filter-link>
        <button-outlined icon-left="plus" @click="$refs.snippetTextModal.open()">
          New Text Snippet
        </button-outlined>
      </template>

      <app-empty-message
        :count="textSnippets.length"
        heading="You have no snippets yet"
      >
        <table-default
          :table-data="textSnippets"
          default-sort="label"
        >
          <b-table-column label="Name" field="label" sortable v-slot="props">
            {{ props.row.label }}
          </b-table-column>

          <b-table-column label="Description" field="description" v-slot="props">
            {{ props.row.description }}
          </b-table-column>

          <b-table-column label="Body" field="body" sortable v-slot="props">
            {{ $utils.truncateString(props.row.body.replace(/(<([^>]+)>)/gi, ""), 70) }}
          </b-table-column>

          <b-table-column cell-class="action_cell" width="200" v-slot="props">
            <button-link @click="$refs.snippetTextModal.open(props.row)">Manage</button-link>
            <button-link :loading="isDuplicatingText" @click="duplicateText(props.row.id)">Duplicate</button-link>
          </b-table-column>
        </table-default>
      </app-empty-message>
    </section-page>

    <modal-container>
      <modal-crud-form
        ref="snippetNoteModal"
        title="Note Snippet"
        form-component="snippet-note-form">
      </modal-crud-form>

      <modal-crud-form
        ref="snippetEmailModal"
        title="Email Snippet"
        form-component="snippet-email-form">
      </modal-crud-form>

      <modal-crud-form
        ref="snippetTextModal"
        title="Text Snippet"
        form-component="snippet-text-form">
      </modal-crud-form>
    </modal-container>
  </div>
</template>

<script>
import ModalCrudForm from "../../components/modal/ModalCrudForm";
import {ENUMS} from "../../services/enums";

export default {
  name: "SnippetsTab",
  components: {ModalCrudForm},
  data() {
    return {
      isDuplicatingNote: false,
      isDuplicatingEmail: false,
      isDuplicatingText: false,
    }
  },
  computed: {
    snippets() {
      return this.$store.getters.snippets;
    },
    noteSnippets() {
      const allSnippets = this.$utils.clone(this.snippets);

      return allSnippets.filter(snippet => snippet.type === ENUMS.SnippetTypeEnum.Note.value);
    },
    emailSnippets() {
      const allSnippets = this.$utils.clone(this.snippets);

      return allSnippets.filter(snippet => snippet.type === ENUMS.SnippetTypeEnum.Email.value);
    },
    textSnippets() {
      const allSnippets = this.$utils.clone(this.snippets);

      return allSnippets.filter(snippet => snippet.type === ENUMS.SnippetTypeEnum.Text.value);
    },
  },
  methods: {
    duplicateNote(snippetId) {
      this.isDuplicatingNote = true;
      this.$api.snippets.clone(snippetId)
        .then(res => {
          this.$buefy.toast.open({
            message: "Copied snippet",
            type: 'is-success'
          });

          this.$refs.snippetNoteModal.open(res.data);
        })
        .finally(() => this.isDuplicatingNote = false);
    },
    duplicateEmail(snippetId) {
      this.isDuplicatingEmail = true;
      this.$api.snippets.clone(snippetId)
        .then(res => {
          this.$buefy.toast.open({
            message: "Copied snippet",
            type: 'is-success'
          });

          this.$refs.snippetEmailModal.open(res.data);
        })
        .finally(() => this.isDuplicatingEmail = false);
    },
    duplicateText(snippetId) {
      this.isDuplicatingText = true;
      this.$api.snippets.clone(snippetId)
        .then(res => {
          this.$buefy.toast.open({
            message: "Copied snippet",
            type: 'is-success'
          });

          this.$refs.snippetTextModal.open(res.data);
        })
        .finally(() => this.isDuplicatingText = false);
    }
  },
}
</script>
